import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { NgForm, ControlContainer } from '@angular/forms';

import moment from 'moment';

import { UtilityService } from '../../../../core/services/utility.service';
import { VistaCustomField, VistaCustomFieldDefinition } from '../../../../type-definitions/general/general';

@Component({
  selector: 'app-ud-field',
  templateUrl: './ud-field.component.html',
  styleUrl: './ud-field.component.css',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class UdFieldComponent implements OnInit, OnChanges { 
  @Input() disabled: boolean = false;
  @Input() get showInput(): boolean {
    return this._showInput;
  }
  set showInput(value: boolean) {
    this._showInput = value;
    this.updateDisplayVisibility(value);
    // if showInput is being set to false, set control value to initial value
    if (!value && this.initialValue !== undefined && this.fieldDefinition) {
      this.setControlValueFromDb(this.initialValue);
    }
  }

  // the vista form to which the custom field belongs
  @Input() vistaForm: string;

  // field to hold the custom field definition
  @Input() fieldDefinitions: VistaCustomFieldDefinition[];
  
  // input field to hold the bound data
  @Input() fieldData: VistaCustomField;
  @Output() fieldDataChange = new EventEmitter<VistaCustomField>();

  _showInput: boolean = true;
  showDisplayNoLink: boolean;
  showDisplayLink: boolean;
  dateFormat: string;
  fieldDefinition: VistaCustomFieldDefinition = null;
  datePickerOptions: object;
  datePickerOptionsMonth: object;
  initialValue: string;
  _controlValue: string;
  displayValue: string;
  fieldDefinitionFound: boolean;

  controlType: { [key: string]: boolean } = {};

  constructor(private utilityService: UtilityService) { }

  ngOnInit() {
    this.dateFormat = this.utilityService.getDateFormat().toUpperCase();

    this.fieldDefinition = this.fieldDefinitions.find(fd => fd.Form == this.vistaForm && fd.ColumnName === this.fieldData.ColumnName);
    this.fieldDefinitionFound = this.fieldDefinition !== undefined && this.fieldDefinition !== null;
    this.updateDisplayVisibility(this.showInput);

    this.datePickerOptions = this.utilityService.getDefaultDatepickerOptions(false);
    this.datePickerOptionsMonth = this.utilityService.getDefaultDatepickerOptions(true);

    this.setControlType();
    this.setControlValueFromDb(this.fieldData.ColumnValue);
    // this is a hack, there's probably a better way/place to do this
    if (this.fieldDefinitionFound && this.fieldDefinition.Type === 'checkbox' && (this.fieldData.ColumnValue === null || this.fieldData.ColumnValue === undefined)) {
      this.fieldData.ColumnValue = 'N';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fieldData']) {
      this.initialValue = this.fieldData.ColumnValue;
      this.setControlValueFromDb(this.fieldData.ColumnValue);
    }
  }

  public updateDisplayVisibility(value: boolean): void {
    if (!this.fieldDefinition)
      return;

    this.showDisplayLink = !value && this.fieldDefinition.Type == 'web';
    this.showDisplayNoLink = !value && this.fieldDefinition.Type != 'web';
  }

  public setControlType(): void {
    this.controlType['date'] = this.fieldDefinition.Type === 'date';
    this.controlType['month'] = this.fieldDefinition.Type === 'month';
    this.controlType['combobox'] = this.fieldDefinition.Type === 'combobox';
    this.controlType['notes'] = this.fieldDefinition.Type === 'notes';
    this.controlType['checkbox'] = this.fieldDefinition.Type === 'checkbox';
    this.controlType['percent'] = this.fieldDefinition.Type === 'percent';
    this.controlType['decimal'] = this.fieldDefinition.Type === 'decimal';
    this.controlType['wholenumber'] = ['byte', 'short', 'int', 'long'].includes(this.fieldDefinition.Type);
    this.controlType['string'] = this.fieldDefinition.Type === 'string';
    this.controlType['web'] = this.fieldDefinition.Type === 'web';
  }

  public get controlValue(): string {
    return this._controlValue;
  }

  public set controlValue(value: string) {
    this._controlValue = value;
    this.setDisplayValue(value);
    this.updateFieldData(value);
  }

  public setControlValueFromDb(value: string): void {
    if (!this.fieldDefinition)
      return;

    if (value === null || value === undefined) {
      if (this.fieldDefinition.Type === 'checkbox')
        this._controlValue = 'N';
      else
        this._controlValue = '';
    }
    else if (this.fieldDefinition.Type === 'date') {
      this._controlValue = moment(value).format(this.dateFormat);
    }
    else if (this.fieldDefinition.Type === 'month') {
      this._controlValue = moment(value).format('MM/YYYY');
    }
    else if (this.fieldDefinition.Type === 'percent') {
      const numVal: number = parseFloat(value);
      const strVal: string = (Math.round(numVal * 10000) / 100).toFixed(2);
      this._controlValue = strVal;
    }
    else {
      this._controlValue = value;
    }

    this.setDisplayValue(value);
  }

  public setDisplayValue(value: string): void {
    if (!this.fieldDefinition)
      return;

    if (this.fieldDefinition.Type === 'checkbox') {
      this.displayValue = value === 'Y' ? 'Yes' : 'No';
    }
    else if (value === null || value === undefined) {
      this.displayValue = '';
    }
    else if (this.fieldDefinition.Type === 'date') {
      this.displayValue = moment(value).format(this.dateFormat);
    }
    else if (this.fieldDefinition.Type === 'month') {
      this.displayValue = moment(value).format('MM/YYYY');
    }
    else if (this.fieldDefinition.Type === 'percent') {
      const numVal: number = parseFloat(value);
      const strVal: string = (Math.round(numVal * 10000) / 100).toFixed(2);
      this.displayValue = strVal + '%';
    }
    else if (this.fieldDefinition.Type === 'combobox') {
      this.displayValue = this.fieldDefinition.ComboTypes?.find(lv => lv.DatabaseValue === value)?.DisplayValue;
    }
    else if (this.fieldDefinition.Type === 'web') {
      let url: string;
      try {
        url = new URL(value).toString();
      }
      catch {
        url = null;
      }
      this.displayValue = url;
    }
    else {
      this.displayValue = value;
    }
  }

  public updateFieldData(value: string): void {
    if (!this.fieldDefinition)
      return;

    if (value === null || value === undefined || value.toString().trim() === '') {
      if (this.fieldDefinition.Type === 'checkbox')
        this.fieldData.ColumnValue = 'N';
      else
        this.fieldData.ColumnValue = null;
    }
    else if (this.fieldDefinition.Type === 'date') {
      this.fieldData.ColumnValue = moment(value).format('YYYY-MM-DD');
    }
    else if (this.fieldDefinition.Type === 'month') {
      this.fieldData.ColumnValue = moment(value).format('YYYY-MM-DD');
    }
    else if (this.fieldDefinition.Type === 'percent') {
      const numVal: number = parseFloat(value);
      this.fieldData.ColumnValue = (Math.round(numVal * 100) / 10000).toString();;
    }
    else {
      this.fieldData.ColumnValue = value;
    }
    
    this.fieldDataChange.emit(this.fieldData);
  }

  public onFieldBlur(): void {
    let value: string = this.controlValue;
    if (this.fieldDefinition.Type === 'web' && value != null && value != '' && (!value.startsWith('http://') && !value.startsWith('https://'))) {
      value = `https://${value}`;
    }

    this.controlValue = value;
  }

  public onOpenCalendar(container) {
    container.monthSelectHandler = (event): void => {
      container._store.dispatch(container._actions.select(event.date));
    };
    container.setViewMode('month');
  }
}
